import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { DEFAULT_LOCALE, LOCALES, parseLocale, resolveLocale, type Locale } from '@29100/shared';
import { formatMessage, type Values } from './format';
import { es, type MessageKey } from './es';
import { en } from './en';

const CATALOGOS: Record<Locale, Record<MessageKey, string>> = { es, en };
const CLAVE = '29100.locale';

interface I18n {
  locale: Locale;
  /** Etiqueta BCP-47 completa para `Intl`: `es-CO`, `en-US`. */
  formatLocale: string;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, values?: Values) => string;
}

const I18nContext = createContext<I18n | null>(null);

/**
 * Idioma de la aplicación.
 *
 * Tres fuentes por orden de prioridad: lo que el usuario eligió a mano, lo que
 * eligió en otro dispositivo —viene del perfil— y lo que dice el navegador.
 * La elección manual gana siempre: quien cambia el idioma espera que se
 * respete aunque su sistema esté en otro.
 *
 * El idioma se separa de la **región**: se traduce a `es`, pero se formatea
 * con la etiqueta completa del navegador (`es-CO`), que es lo que hace que una
 * fecha se lea natural en cada país sin duplicar el catálogo.
 */
export function I18nProvider({
  children,
  userLocale,
}: {
  children: ReactNode;
  /** Preferencia guardada en el perfil, si hay sesión. */
  userLocale?: string | null;
}) {
  const [elegido, setElegido] = useState<Locale | null>(() =>
    parseLocale(localStorage.getItem(CLAVE)),
  );

  const locale = useMemo(
    () => resolveLocale(elegido, userLocale, navigator.language),
    [elegido, userLocale],
  );

  // La región sólo se conserva si coincide con el idioma activo: mostrar
  // fechas al estilo colombiano en una interfaz en inglés sería peor que usar
  // el formato por defecto del idioma.
  const formatLocale = useMemo(() => {
    const delNavegador = navigator.language;
    return parseLocale(delNavegador) === locale ? delNavegador : locale;
  }, [locale]);

  useEffect(() => {
    // Lo leen los lectores de pantalla para pronunciar y los traductores
    // automáticos para no traducir dos veces.
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((siguiente: Locale) => {
    localStorage.setItem(CLAVE, siguiente);
    setElegido(siguiente);
  }, []);

  const t = useCallback(
    (key: MessageKey, values?: Values) => {
      const catalogo = CATALOGOS[locale] ?? CATALOGOS[DEFAULT_LOCALE];
      // Si faltara una clave se cae al español y, en último extremo, se
      // devuelve la propia clave: un identificador en pantalla se localiza y
      // se corrige; una cadena vacía pasa inadvertida.
      const plantilla = catalogo[key] ?? es[key] ?? key;
      return formatMessage(plantilla, values, formatLocale);
    },
    [locale, formatLocale],
  );

  const value = useMemo<I18n>(
    () => ({ locale, formatLocale, setLocale, t }),
    [locale, formatLocale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n debe usarse dentro de <I18nProvider>');
  return context;
}

/** Atajo para el caso habitual: sólo traducir. */
export function useT(): I18n['t'] {
  return useI18n().t;
}

export { LOCALES };
export type { MessageKey };
